import { Platform } from 'react-native';
import type { TonyteTheme } from './types';

/** The existing Tonyte palette, preserved byte-for-byte as the default theme. */
export const midnightTheme: TonyteTheme = {
  name: 'midnight',
  isDark: true,
  statusBarStyle: 'light-content',
  colors: {
    background: '#070b14',
    backgroundSecondary: '#0A0E17',
    surface: '#0f1520',
    surfaceSoft: '#0f1520',
    surfaceElevated: '#161B26',
    surfaceGlass: 'rgba(16,23,34,0.62)',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.65)',
    textMuted: 'rgba(255,255,255,0.4)',
    textPlaceholder: 'rgba(255,255,255,0.35)',
    bioText: 'rgba(255,255,255,0.82)',
    bioPlaceholder: 'rgba(255,255,255,0.45)',
    actionLabel: 'rgba(255,255,255,0.82)',
    sectionHeading: '#ffffff',
    chipText: 'rgba(255,255,255,0.72)',
    chipSelectedText: '#26D0CE',
    border: 'rgba(255,255,255,0.1)',
    borderStrong: 'rgba(255,255,255,0.18)',
    cardBorder: 'rgba(255,255,255,0.1)',
    accent: '#F27D26',
    accentSecondary: '#26D0CE',
    accentSecondarySoft: 'rgba(38,208,206,0.07)',
    accentSecondaryBorder: 'rgba(38,208,206,0.22)',
    inputBackground: 'rgba(255,255,255,0.07)',
    controlBackground: 'rgba(255,255,255,0.07)',
    chipBackground: 'rgba(255,255,255,0.05)',
    chipBackgroundActive: 'rgba(242,125,38,0.20)',
    filterActiveIcon: 'rgba(255,255,255,0.92)',
    navBackground: 'rgba(3,6,14,0.9)',
    navIcon: 'rgba(221,227,234,0.78)',
    navIconActive: '#ff8a22',
    cardBackground: 'rgba(4,8,15,0.72)',
    mediaActionBackground: 'rgba(10,14,22,0.58)',
    mediaActionBorder: 'rgba(255,255,255,0.18)',
    mediaActionIcon: '#FFFFFF',
    overlay: 'rgba(3,6,14,0.6)',
    divider: 'rgba(255,255,255,0.1)',
    icon: 'rgba(255,255,255,0.78)',
    iconSubtle: 'rgba(255,255,255,0.68)',
    iconFaint: 'rgba(255,255,255,0.20)',
    iconMuted: 'rgba(255,255,255,0.35)',
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
  },
  gradients: {
    background: ['#07101d', '#0a0e17', '#050812'],
    surface: ['rgba(255,255,255,0.075)', 'rgba(16,23,34,0.72)', 'rgba(7,11,20,0.82)'],
    surfaceElevated: ['rgba(255,255,255,0.10)', 'rgba(22,27,38,0.92)', 'rgba(10,14,23,0.96)'],
    control: ['rgba(255,255,255,0.10)', 'rgba(16,23,34,0.92)', 'rgba(7,11,20,0.96)'],
    accent: ['#ffae5f', '#f27d26', '#c84e08'],
    navBase: ['rgba(20,37,58,0.88)', 'rgba(7,13,25,0.94)', 'rgba(2,5,11,0.98)'],
    navAccent: ['rgba(38,208,206,0.20)', 'rgba(38,208,206,0.055)', 'rgba(38,208,206,0)'],
    navGlass: ['rgba(255,255,255,0.075)', 'rgba(255,255,255,0.016)', 'rgba(255,255,255,0)'],
    navWarm: ['rgba(242,125,38,0)', 'rgba(242,125,38,0.065)', 'rgba(242,125,38,0.035)'],
    navButtonActive: ['rgba(255,151,54,0.30)', 'rgba(242,125,38,0.105)', 'rgba(7,10,17,0.88)'],
    navButtonInactive: ['rgba(255,255,255,0.052)', 'rgba(7,13,24,0.86)', 'rgba(4,8,15,0.9)'],
  },
  shadows: {
    card: {
      shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.35,
      shadowRadius: 10, elevation: 8,
    },
    elevated: {
      shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.5,
      shadowRadius: 16, elevation: 12,
    },
    navigation: {
      shadowColor: '#000', shadowOffset: { width: 0, height: -12 },
      shadowOpacity: Platform.OS === 'ios' ? 0.42 : 0.56,
      shadowRadius: Platform.OS === 'ios' ? 28 : 32, elevation: 18,
    },
  },
  eventCard: {
    shellBackground: 'rgba(7,16,29,0.86)',
    shellBorder: 'rgba(255,255,255,0.13)',
    edgeHighlight: 'transparent',
    fallbackGradient: ['#14263b', '#101722', '#070b14'],
    imageOverlay: ['rgba(2,5,12,0.04)', 'rgba(2,5,12,0.15)', 'rgba(2,5,12,0.72)', 'rgba(2,5,12,0.96)'],
    colorWash: ['rgba(67,94,235,0.35)', 'rgba(242,125,38,0.14)', 'rgba(2,5,12,0)'],
    colorWashOpacity: 0.7,
    headerBackground: 'rgba(3,8,16,0.72)',
    headerBorder: 'rgba(255,255,255,0.10)',
    headerTitle: '#FFFFFF',
    headerMeta: 'rgba(255,255,255,0.84)',
    headerTextShadow: 'rgba(0,0,0,0.45)',
    categoryBackground: 'rgba(255,255,255,0.12)',
    categoryBorder: 'rgba(255,255,255,0.14)',
    categoryText: 'rgba(255,255,255,0.78)',
    infoGradient: ['rgba(0,0,0,0.72)', 'rgba(0,0,0,0.52)', 'rgba(0,0,0,0.18)'],
    infoBorder: 'rgba(255,255,255,0.06)',
    infoPrimaryText: 'rgba(255,255,255,0.93)',
    infoSecondaryText: 'rgba(255,255,255,0.60)',
    entryText: 'rgba(255,185,144,0.94)',
    chipBackground: 'rgba(5,8,14,0.48)',
    chipBorder: 'rgba(255,255,255,0.12)',
    chipText: 'rgba(226,232,240,0.86)',
    bottomStatusText: 'rgba(255,255,255,0.74)',
    bottomMutedText: 'rgba(255,255,255,0.58)',
    manageBackground: 'rgba(10,14,22,0.55)',
    manageBorder: 'rgba(255,255,255,0.18)',
    manageText: '#FFFFFF',
    deleteBackground: 'rgba(10,14,22,0.55)',
    deleteBorder: 'rgba(255,255,255,0.18)',
    deleteText: '#F27D26',
  },
};
